import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Banknote, Smartphone, Shield, Package, CreditCard, Building2,
  Save, X, History,
} from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR } from "@/lib/api";

/* ─────────────────────────────────────────────────────────────────
   PaymentModeEditor — single dialog used everywhere a payment's mode
   can be changed after it was recorded (Billing, Reports, OPD, IPD,
   PatientProfile).  Opens with the current mode highlighted and a
   mandatory audit reason.  Shows the row's edit_history too so the
   admin can see who changed what, when, and why.

   Props:
     payment  — the payment doc (needs id, mode, amount, reference_no,
                receipt_no, patient_name, edit_history?).
     onClose  — call to dismiss the dialog.
     onSaved  — called after a successful PUT so the parent can refresh.

   Primary modes requested by the user are rendered as big tiles:
     CASH · UPI · PMJAY · PACKAGE
   Secondary modes (GPay / Card / Bank) are in the "Other mode" dropdown
   so the UI stays uncluttered.
───────────────────────────────────────────────────────────────── */

const PRIMARY_MODES = [
  { key: "Cash",    label: "CASH",    icon: Banknote,   color: "emerald" },
  { key: "UPI",     label: "UPI",     icon: Smartphone, color: "indigo"  },
  { key: "PMJAY",   label: "PMJAY",   icon: Shield,     color: "teal"    },
  { key: "PACKAGE", label: "PACKAGE", icon: Package,    color: "amber"   },
];
const SECONDARY_MODES = ["GPay", "Card", "Bank"];
const NEEDS_REF = new Set(["UPI", "GPay", "Card", "PMJAY", "PACKAGE"]);

const COLOR_CLASSES = {
  emerald: { on: "border-emerald-600 bg-emerald-50 text-emerald-700", off: "border-slate-300 hover:border-emerald-400" },
  indigo:  { on: "border-indigo-600 bg-indigo-50 text-indigo-700",    off: "border-slate-300 hover:border-indigo-400" },
  teal:    { on: "border-teal-600 bg-teal-50 text-teal-700",          off: "border-slate-300 hover:border-teal-400" },
  amber:   { on: "border-amber-600 bg-amber-50 text-amber-800",       off: "border-slate-300 hover:border-amber-500" },
  slate:   { on: "border-slate-700 bg-slate-100 text-slate-800",      off: "border-slate-300 hover:border-slate-500" },
};

export default function PaymentModeEditor({ payment, onClose, onSaved }) {
  const existing = payment?.mode || payment?.payment_mode || "Cash";
  const existingNormalized = (PRIMARY_MODES.find(p => p.key.toLowerCase() === existing.toLowerCase())?.key)
    || (SECONDARY_MODES.find(m => m.toLowerCase() === existing.toLowerCase()))
    || "Cash";

  const [mode, setMode] = useState(existingNormalized);
  const [reference, setReference] = useState(payment?.reference_no || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  if (!payment) return null;

  const isPrimary = PRIMARY_MODES.some(p => p.key === mode);
  const dirty = mode !== existingNormalized || (reference || "") !== (payment.reference_no || "");
  const needsRef = NEEDS_REF.has(mode);

  const save = async () => {
    if (!dirty) { toast.error("No changes to save"); return; }
    if (!reason.trim()) { toast.error("Please enter a reason for the mode change"); return; }
    setSaving(true);
    try {
      await api.put(`/payments/${payment.id}`, {
        mode: mode.trim(),
        reason: reason.trim(),
        reference_no: reference.trim(),
      });
      toast.success(`Payment mode updated → ${mode.toUpperCase()}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Edit failed");
    } finally {
      setSaving(false);
    }
  };

  const history = payment.edit_history || [];

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="payment-mode-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <History className="w-4 h-4 text-slate-500" />
            Change Payment Mode
          </DialogTitle>
        </DialogHeader>

        {/* Receipt summary */}
        <div className="bg-slate-50 border rounded-sm p-3 text-xs space-y-1">
          <div className="flex justify-between">
            <span className="text-slate-500">Receipt</span>
            <span className="font-mono font-semibold">{payment.receipt_no || payment.id?.slice(0, 8)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Patient</span>
            <span className="font-medium">{payment.patient_name || "—"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Amount</span>
            <span className="font-mono font-semibold text-slate-900">{fmtINR(payment.amount || 0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Current Mode</span>
            <span className="font-mono font-semibold uppercase">{existing}</span>
          </div>
        </div>

        {/* Primary modes */}
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">New Payment Mode *</Label>
          <div className="grid grid-cols-4 gap-2 mt-1">
            {PRIMARY_MODES.map((m) => {
              const Icon = m.icon;
              const active = mode === m.key;
              const cls = COLOR_CLASSES[m.color];
              return (
                <button key={m.key} type="button"
                  data-testid={`pay-mode-${m.key}`}
                  onClick={() => setMode(m.key)}
                  className={`border-2 rounded-sm p-2 text-center transition ${active ? cls.on + " shadow-sm" : "bg-white " + cls.off + " text-slate-600"}`}
                >
                  <Icon className="w-5 h-5 mx-auto mb-1" />
                  <div className="text-[11px] font-semibold">{m.label}</div>
                </button>
              );
            })}
          </div>

          {/* Secondary modes dropdown */}
          <div className="mt-2 flex items-center gap-2">
            <span className="text-[10px] text-slate-400">Other:</span>
            {SECONDARY_MODES.map((m) => (
              <button key={m} type="button"
                data-testid={`pay-mode-${m}`}
                onClick={() => setMode(m)}
                className={`px-2.5 py-1 text-[11px] border rounded-sm ${mode === m ? COLOR_CLASSES.slate.on : "bg-white " + COLOR_CLASSES.slate.off + " text-slate-600"}`}>
                {m}
              </button>
            ))}
          </div>
        </div>

        {/* Reference No (shown for non-cash modes) */}
        {needsRef && (
          <div>
            <Label>{mode === "PMJAY" ? "PMJAY TMS / Case No." : mode === "PACKAGE" ? "Package Reference" : mode === "UPI" || mode === "GPay" ? "UPI Txn ID" : mode === "Card" ? "Card Last 4" : "Reference"}</Label>
            <Input
              data-testid="pay-mode-ref"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={mode === "UPI" || mode === "GPay" ? "e.g. 403824156789" : mode === "Card" ? "e.g. 4242" : mode === "PMJAY" ? "e.g. TMS2026/1234" : "Optional"}
              className="h-9 font-mono"
            />
          </div>
        )}

        {/* Reason */}
        <div>
          <Label>Reason for change (audit-logged) *</Label>
          <Input
            data-testid="pay-mode-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Patient actually paid UPI, not cash"
            className="h-9"
            autoFocus
          />
        </div>

        {/* Edit history */}
        {history.length > 0 && (
          <div className="border-t pt-2 mt-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
              <History className="w-3 h-3" /> Previous Edits ({history.length})
            </div>
            <div className="max-h-28 overflow-y-auto space-y-1 pr-1">
              {history.slice(-5).reverse().map((h, i) => (
                <div key={i} className="text-[10px] text-slate-600 border-l-2 border-slate-200 pl-2">
                  <div><b>{h.from_mode || "—"}</b> → <b>{h.to_mode || "—"}</b> <span className="text-slate-400">· {h.at ? new Date(h.at).toLocaleString() : ""}</span></div>
                  <div className="text-slate-500 italic">by {h.by_name} — {h.reason}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="border-t pt-3 mt-1">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            <X className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
          <Button data-testid="pay-mode-save" onClick={save} disabled={saving || !dirty}
            className="bg-emerald-700 hover:bg-emerald-800">
            <Save className="w-3.5 h-3.5 mr-1" />
            {saving ? "Saving…" : "Save Change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
